import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/** @typedef {{id: string, available: boolean, options: string[]}} CardVariant */

/** Size selection enhancement; native product-form-component owns all cart requests. */
export class InlineSizePicker extends Component {
  /** @type {AbortController | null} */
  #controller = null;
  /** @type {CardVariant[]} */
  #variants = [];
  /** @type {CardVariant | undefined} */
  #selectedVariant;
  #ready = false;
  #selectionPending = false;
  #unresolvedSelection = false;
  #busy = false;
  #generation = 0;
  #cartGeneration = 0;
  /** @type {Promise<any> | null} */
  #selectionPromise = null;
  /** @type {Promise<any> | null} */
  #cartPromise = null;

  get #card() { return this.closest('product-card'); }
  get #panel() { return /** @type {HTMLElement | null} */ (this.querySelector('[data-inline-panel]')); }
  get #trigger() {
    return /** @type {HTMLButtonElement | null} */ (this.closest('quick-add-component')?.querySelector('[data-inline-size-trigger]'));
  }
  get #buttons() { return this.querySelectorAll('button[data-inline-size]'); }

  connectedCallback() {
    super.connectedCallback();
    this.#connect();
  }

  #connect() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#loadVariants();
    this.#card?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
    this.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });
    this.addEventListener('keydown', this.#onKeydown, { signal });
    document.addEventListener('pointerdown', this.#onOutsidePointer, { signal });
    document.addEventListener('focusin', this.#onOutsideFocus, { signal });
    // Keep the native full picker usable while the product-form module is loading.
    const previousSelection = this.#selectionPromise;
    const previousCart = this.#cartPromise;
    Promise.allSettled([customElements.whenDefined('product-form-component'), previousSelection, previousCart]).then((results) => {
      if (signal.aborted || !this.isConnected) return;
      this.#loadVariants();
      if (this.#selectionPromise === previousSelection) {
        this.#selectionPromise = null;
        this.#selectionPending = false;
        if (results[1]?.status === 'rejected') this.#unresolvedSelection = true;
      }
      if (this.#cartPromise === previousCart) {
        this.#cartPromise = null;
        this.#busy = false;
      }
      this.#ready = this.#variants.length > 0 && Boolean(this.#card);
      this.#render();
      if (previousCart && results[2]?.status === 'rejected') {
        this.#showError();
        this.open(false);
      } else if (previousCart && results[2]?.status === 'fulfilled' && results[2].value?.detail?.didError) {
        this.open(false);
      }
    });
  }

  updatedCallback() {
    super.updatedCallback();
    this.#generation++;
    this.#cartGeneration++;
    this.#ready = false;
    this.#controller?.abort();
    this.#close(false);
    this.#connect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#generation++;
    this.#cartGeneration++;
    this.#ready = false;
    this.#busy = false;
    this.#selectionPending = false;
    this.#close(false);
  }

  #loadVariants() {
    try {
      const variants = JSON.parse(this.querySelector('[data-inline-variants]')?.textContent || '[]');
      this.#variants = Array.isArray(variants) && variants.length <= 50
        ? variants.filter((variant) => typeof variant.id === 'string' && typeof variant.available === 'boolean' && Array.isArray(variant.options))
        : [];
    } catch {
      this.#variants = [];
    }
    const checkedSwatch = /** @type {HTMLInputElement | null} */ (
      this.#card?.querySelector('swatches-variant-picker-component input:checked[data-variant-id]')
    );
    const selectedId = checkedSwatch?.dataset.variantId ?? this.dataset.initialVariant;
    this.#selectedVariant = this.#variants.find((variant) => variant.id === selectedId);
    this.#unresolvedSelection = !this.#selectedVariant;
  }

  /** Returns false to keep the native full picker as the fallback. @param {boolean} [moveFocus] */
  open(moveFocus = true) {
    if (!this.#ready || this.#unresolvedSelection || !this.#panel || !this.#trigger) return false;
    this.#panel.hidden = false;
    this.#trigger.setAttribute('aria-controls', this.#panel.id);
    this.#trigger.setAttribute('aria-expanded', 'true');
    this.closest('quick-add-component')?.setAttribute('data-inline-open', '');
    this.#render();
    const target = this.querySelector('button[data-inline-size]:not(:disabled)') || this.querySelector('[data-inline-close]');
    if (moveFocus && target instanceof HTMLElement) target.focus({ preventScroll: true });
    return true;
  }

  /** Close button and Escape restore focus to the persistent plus control. */
  close = () => { this.#close(true); };

  /** @param {boolean} restoreFocus */
  #close(restoreFocus) {
    if (this.#panel) this.#panel.hidden = true;
    this.#trigger?.setAttribute('aria-expanded', 'false');
    this.closest('quick-add-component')?.removeAttribute('data-inline-open');
    if (restoreFocus) this.#trigger?.focus({ preventScroll: true });
  }

  /** @param {string | undefined} size */
  #variantForSize(size) {
    const sizeIndex = Number(this.dataset.sizePosition) - 1;
    const colorIndex = Number(this.dataset.colorPosition) - 1;
    return this.#variants.find((variant) => variant.options[sizeIndex] === size &&
      (colorIndex < 0 || variant.options[colorIndex] === this.#selectedVariant?.options[colorIndex]));
  }

  #render() {
    const blocked = !this.#ready || this.#busy || this.#selectionPending || this.#unresolvedSelection;
    this.#panel?.setAttribute('aria-busy', String(this.#busy || this.#selectionPending));
    for (const button of this.#buttons) {
      if (!(button instanceof HTMLButtonElement)) continue;
      const variant = this.#variantForSize(button.dataset.inlineSize);
      button.disabled = blocked || !variant?.available;
      button.toggleAttribute('data-unavailable', !variant?.available);
    }
  }

  /** @param {MouseEvent} event */
  addSize = (event) => {
    event.preventDefault();
    const button = event.target instanceof Element ? event.target.closest('button[data-inline-size]') : null;
    if (!(button instanceof HTMLButtonElement) || !this.contains(button) || button.disabled ||
      !this.#ready || this.#busy || this.#selectionPending || this.#unresolvedSelection) return;
    const variant = this.#variantForSize(button.dataset.inlineSize);
    if (!variant?.available) return;
    const form = this.querySelector('form');
    const variantInput = this.querySelector('input[name="id"]');
    if (!(form instanceof HTMLFormElement) || !(variantInput instanceof HTMLInputElement)) return;

    variantInput.value = variant.id;
    variantInput.disabled = false;
    this.#busy = true;
    this.#clearError();
    this.#render();
    // The cart drawer may open synchronously. Let it retain a visible return-focus target.
    this.#close(true);
    try {
      form.requestSubmit();
    } catch {
      this.#busy = false;
      this.#showError();
      this.open(false);
      this.#render();
    }
  };

  /** @param {import('@shopify/events').ProductSelectEvent} event */
  #onProductSelect = async (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card') !== this.#card) return;
    const generation = ++this.#generation;
    this.#selectionPromise = event.promise;
    this.#selectionPending = true;
    this.#render();
    this.#announce(this.dataset.loadingMessage || '');
    try {
      const { detail } = await event.promise;
      // The native form consumes this same promise and clears its variant-change queue first.
      await Promise.resolve();
      if (!this.isConnected || generation !== this.#generation) return;
      const variant = this.#variants.find((item) => item.id === String(detail?.resource?.id));
      const productId = detail?.newProduct?.id ?? detail?.productId ?? this.dataset.productId;
      if (!variant || String(productId) !== this.dataset.productId) throw new Error('Unresolved card selection');
      this.#selectedVariant = variant;
      this.#unresolvedSelection = false;
      this.#clearError();
    } catch {
      if (!this.isConnected || generation !== this.#generation) return;
      this.#unresolvedSelection = true;
      this.#close(this.contains(document.activeElement));
    } finally {
      if (this.isConnected && generation === this.#generation) {
        this.#selectionPromise = null;
        this.#selectionPending = false;
        this.#announce('');
        this.#render();
      }
    }
  };

  /** @param {import('@shopify/events').CartLinesUpdateEvent} event */
  #onCartUpdate = async (event) => {
    if (!(event.target instanceof Element) || !this.contains(event.target) || event.action !== 'add') return;
    const generation = ++this.#cartGeneration;
    this.#cartPromise = event.promise;
    this.#busy = true;
    this.#render();
    try {
      const { detail } = await event.promise;
      if (!this.isConnected || generation !== this.#cartGeneration) return;
      if (detail?.didError) {
        this.open(false); // The native form displays and announces Shopify's error.
        return;
      }
      this.#close(false);
      this.#announce(this.dataset.addedMessage || '');
    } catch {
      if (this.isConnected && generation === this.#cartGeneration) {
        this.#cartPromise = null;
        this.#showError();
        this.open(false);
      }
    } finally {
      if (this.isConnected && generation === this.#cartGeneration) {
        this.#busy = false;
        this.#render();
      }
    }
  };

  #clearError() {
    const error = this.querySelector('[data-inline-error]');
    if (error) { error.textContent = ''; error.classList.add('hidden'); }
    this.#announce('');
  }

  #showError() {
    const error = this.querySelector('[data-inline-error]');
    if (error) { error.textContent = this.dataset.errorMessage || ''; error.classList.remove('hidden'); }
    this.#announce(this.dataset.errorMessage || '');
  }

  /** The native form's single live region also owns inline feedback. @param {string} message */
  #announce(message) {
    const liveRegion = this.querySelector('[ref="liveRegion"]');
    if (liveRegion) liveRegion.textContent = message;
  }

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'Escape' || this.#panel?.hidden) return;
    event.preventDefault();
    event.stopPropagation();
    this.#close(true);
  };

  /** @param {PointerEvent} event */
  #onOutsidePointer = (event) => {
    if (event.target instanceof Node && !this.#card?.contains(event.target)) this.#close(false);
  };

  /** @param {FocusEvent} event */
  #onOutsideFocus = (event) => {
    if (event.target instanceof Node && !this.#card?.contains(event.target)) this.#close(false);
  };
}

if (!customElements.get('inline-size-picker-component')) {
  customElements.define('inline-size-picker-component', InlineSizePicker);
}
